/**
 * Answers rendered as a readable list — grouped under the form's own headings, with
 * type-aware values (links for email / phone, badges for yes/no and consent, chips
 * for multi-choice, stars for ratings, masked sensitive answers).
 */

import { Fragment, type ReactNode } from 'react';
import { IconLock, IconCircleCheck, IconCircleX, IconPaperclip, IconStarFilled, IconStar } from '@tabler/icons-react';
import type { FormEntryFileView, FormFieldView } from '@actschurch/ccms-sdk';
import { Anchor, Badge, Group, Stack, Text, SimpleGrid, Box } from '@/components/ui';
import { Divider } from '@/components/ui/data-display';
import { formatAnswer, formatStamp, readKey, stampToDate } from './formsUtils';

const LAYOUT = new Set(['divider', 'spacer', 'pagebreak', 'paragraph']);

interface EntryAnswersProps {
  fields: FormFieldView[];
  answers: Record<string, unknown> | null | undefined;
  files?: FormEntryFileView[];
  sensitiveMasked?: boolean;
  /** Two columns on wide screens. */
  columns?: 1 | 2;
  /** Hide people (participants) fields — shown separately. */
  hideParticipants?: boolean;
}

function isEmpty(v: unknown) {
  return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
}

export function AnswerValue({ field, value, files, masked }: { field: FormFieldView; value: unknown; files?: FormEntryFileView[]; masked?: boolean }): ReactNode {
  if (field.isSensitive && masked) {
    return (
      <Group gap={4}>
        <IconLock size={13} />
        <Text size="sm" c="dimmed">
          Hidden — sensitive answer
        </Text>
      </Group>
    );
  }
  const code = field.fieldTypeCode;
  if (code === 'file' || code === 'image') {
    const own = (files ?? []).filter((f) => f.fieldKey === field.fieldKey);
    if (!own.length) return <Text size="sm" c="dimmed">No file</Text>;
    return (
      <Stack gap={2}>
        {own.map((f) => (
          <Group key={f.formEntryFileID} gap={6} wrap="nowrap">
            <IconPaperclip size={13} />
            <Text size="sm" style={{ wordBreak: 'break-all' }}>{f.fileName || f.fileId}</Text>
            {f.fileSize ? <Text size="xs" c="dimmed">{Math.max(1, Math.round(f.fileSize / 1024))} KB</Text> : null}
          </Group>
        ))}
      </Stack>
    );
  }
  if (isEmpty(value)) return <Text size="sm" c="dimmed">—</Text>;

  if (code === 'consent' || code === 'checkbox' || code === 'switch' || typeof value === 'boolean') {
    const yes = value === true || value === 'true';
    const label = code === 'consent' ? (yes ? 'Agreed' : 'Not agreed') : yes ? 'Yes' : 'No';
    return (
      <Badge variant="light" color={yes ? 'green' : 'gray'} leftSection={yes ? <IconCircleCheck size={12} /> : <IconCircleX size={12} />} styles={{ root: { textTransform: 'none' } }}>
        {label}
      </Badge>
    );
  }
  if (code === 'email' && typeof value === 'string') {
    return <Anchor href={`mailto:${value}`} size="sm">{value}</Anchor>;
  }
  if (code === 'phone' && typeof value === 'string') {
    return <Anchor href={`tel:${value.replace(/[^\d+]/g, '')}`} size="sm">{value}</Anchor>;
  }
  if (code === 'rating' && (typeof value === 'number' || /^\d+$/.test(String(value)))) {
    const n = Number(value);
    const max = Math.max(5, n);
    return (
      <Group gap={2}>
        {Array.from({ length: max }, (_, i) =>
          i < n ? <IconStarFilled key={i} size={15} color="var(--mantine-color-yellow-5)" /> : <IconStar key={i} size={15} color="var(--mantine-color-dimmed)" />
        )}
        <Text size="xs" c="dimmed" ml={4}>{n} / {max}</Text>
      </Group>
    );
  }
  if ((code === 'date' || code === 'datetime') && (typeof value === 'string' || typeof value === 'number')) {
    const d = typeof value === 'number' ? stampToDate(value) : new Date(value);
    if (d && !Number.isNaN(d.getTime())) {
      return <Text size="sm">{code === 'date' ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : formatStamp(Number(value))}</Text>;
    }
  }
  if (Array.isArray(value) && value.every((v) => typeof v !== 'object' || v === null)) {
    return (
      <Group gap={4}>
        {value.map((v) => (
          <Badge key={String(v)} variant="light" color="gray" styles={{ root: { textTransform: 'none', fontWeight: 500 } }}>
            {field.options?.find((o) => o.value === String(v))?.label ?? String(v)}
          </Badge>
        ))}
      </Group>
    );
  }
  if (code === 'participants' && Array.isArray(value)) {
    return <Text size="sm">{value.length} {value.length === 1 ? 'person' : 'people'} — listed under People</Text>;
  }
  return (
    <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {formatAnswer(value, field.options)}
    </Text>
  );
}

/** Answers grouped by the form's headings. */
export function EntryAnswers({ fields, answers, files, sensitiveMasked, columns = 1, hideParticipants }: EntryAnswersProps) {
  const top = fields
    .filter((f) => !f.parentKey && !LAYOUT.has(f.fieldTypeCode))
    .filter((f) => !(hideParticipants && f.fieldTypeCode === 'participants'))
    .sort((a, b) => a.pageIndex - b.pageIndex || a.sortOrder - b.sortOrder);

  // Split into sections at each heading.
  const sections: { title: string | null; items: FormFieldView[] }[] = [];
  for (const f of top) {
    if (f.fieldTypeCode === 'heading') {
      sections.push({ title: f.label || null, items: [] });
      continue;
    }
    if (sections.length === 0) sections.push({ title: null, items: [] });
    sections[sections.length - 1].items.push(f);
  }
  const visible = sections.filter((s) => s.items.length > 0);

  if (visible.length === 0) {
    return <Text size="sm" c="dimmed">No answers.</Text>;
  }

  return (
    <Stack gap="lg">
      {visible.map((s, si) => (
        <Fragment key={si}>
          {s.title && (
            <Divider
              label={<Text size="xs" fw={700} tt="uppercase" c="dimmed">{s.title}</Text>}
              labelPosition="left"
              mb={-6}
            />
          )}
          <SimpleGrid cols={{ base: 1, md: columns }} spacing="md" verticalSpacing="md">
            {s.items.map((f) => (
              <Box key={f.fieldKey} style={{ minWidth: 0 }}>
                <Group gap={6} mb={2} wrap="nowrap">
                  <Text size="xs" c="dimmed" fw={600} lineClamp={2}>
                    {f.label || f.fieldKey}
                  </Text>
                  {f.isSensitive && (
                    <Badge size="xs" variant="light" color="orange" leftSection={<IconLock size={10} />} styles={{ root: { textTransform: 'none' } }}>
                      Sensitive
                    </Badge>
                  )}
                </Group>
                <AnswerValue field={f} value={readKey(answers, f.fieldKey)} files={files} masked={sensitiveMasked} />
              </Box>
            ))}
          </SimpleGrid>
        </Fragment>
      ))}
    </Stack>
  );
}
